import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiYAxisChartType,
  ApiMetric,
} from "@incendium/api";
import { Settings } from "@mui/icons-material";
import {
  Button,
  Grid,
  Portal,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Loading from "Components/Loading/Loading";
import { AnalyticsSpacing, PageSubAction } from "consts";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import { FilterAlt } from "@mui/icons-material";
import {
  InsightsBlock,
  useSelectedMetric,
  MetricExplorerDrawer,
  useSelectedAttributes,
} from "features/analytics";
import { useCallback, useState } from "react";
import { useMount } from "react-use";
import { useMetricName } from "features/analytics/hooks/useMetricName";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import { StyledDivider } from "Components/UI/StyledDivider";
import AnalyticsCardGroup from "./AnalyticsCardGroup";

function MetricExplorer() {
  const { selectedMetric, setSelectedMetric } = useSelectedMetric();
  const { selectedAttributes, setSelectedAttributes } = useSelectedAttributes();
  const [open, setOpen] = useState(false);
  const [searchParamsLoaded, setSearchParamsLoaded] = useState(false);
  const metricToName = useMetricName();
  const dimensionToName = useDimensionName();
  useMount(() => {
    const interval = setInterval(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("at") && params.has("m")) {
        setSearchParamsLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  });

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (!searchParamsLoaded) {
    return <Loading fullHeight />;
  }

  return (
    <>
      <Portal container={() => document.getElementById(PageSubAction)}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: "100%", marginX: 0, minWidth: 550 }}
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Tooltip
            title={
              <Stack spacing={1}>
                <Typography color="inherit" variant="subtitle2">
                  {metricToName(selectedMetric as ApiMetric)}
                </Typography>
                {selectedAttributes.length > 0 && (
                  <>
                    <StyledDivider />
                    <Typography color="inherit" variant="subtitle2">
                      Filters
                    </Typography>
                    <Stack>
                      {selectedAttributes.map((attribute) => (
                        <Typography
                          key={attribute.key! + attribute.value!}
                          color="inherit"
                          variant="body2"
                        >
                          {dimensionToName(attribute.key as ApiDimension)} -{" "}
                          {attribute.value}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>
            }
            arrow
            placement="left"
          >
            <FilterAlt color="primary" />
          </Tooltip>

          <Button
            size="small"
            startIcon={<Settings />}
            onClick={() => setOpen(true)}
          >
            Configure
          </Button>
        </Stack>
      </Portal>

      {selectedMetric ? (
        <Stack spacing={6} pb={4}>
          <AnalyticsCardGroup
            localStorageKey="metric-explorer-overview"
            title="Overview"
            renderBody={() => (
              <Grid container spacing={AnalyticsSpacing}>
                <Grid item xs={8} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "Over Time",
                      dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      comparison: true,
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [selectedMetric],
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledMetricClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <AnalyticsCard
                    chart={{
                      name: "By Week",
                      dimension: [ApiDimension.DIMENSION_SESSION_START_BY_WEEK],
                      template: ApiChartTemplate.TREND,
                      type: ApiChartType.GRAPH,
                      comparison: true,
                      attributes: selectedAttributes || [],
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [selectedMetric],
                          chart: {
                            [selectedMetric]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledMetricClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={4} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "New vs Returning",
                      dimension: [ApiDimension.DIMENSION_VISITOR_TYPE],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [selectedMetric],
                          chart: {
                            [selectedMetric]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledMetricClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={8} sx={{ minHeight: 400 }}>
                  <AnalyticsCard
                    chart={{
                      name: "By Channel",
                      dimension: [ApiDimension.DIMENSION_CHANNEL],
                      template: ApiChartTemplate.SNAPSHOT,
                      type: ApiChartType.GRAPH,
                      attributes: selectedAttributes || [],
                      includePublisherReadyOnly: true,
                      yAxisKeys: [
                        {
                          key: "l",
                          fields: [selectedMetric],
                          chart: {
                            [selectedMetric]: ApiYAxisChartType.BAR,
                          },
                        },
                      ],
                      displayOptions: {
                        noAttributionToggle: true,
                        disabledMetricClick: true,
                        noFilters: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            )}
          />
          <AnalyticsCardGroup
            localStorageKey="metric-explorer-traffic-source-insights"
            title="Traffic Source Insights"
            dimensions={[
              ApiDimension.DIMENSION_CHANNEL,
              ApiDimension.DIMENSION_SOURCE,
              ApiDimension.DIMENSION_MEDIUM,
              ApiDimension.DIMENSION_CAMPAIGN,
              ApiDimension.DIMENSION_CONTENT,
              ApiDimension.DIMENSION_TERM,
            ]}
            renderBodyWithDimension={(dimension) => (
              <InsightsBlock dimension={dimension} />
            )}
          />
          <AnalyticsCardGroup
            localStorageKey="metric-explorer-content-insights"
            title="Content Insights"
            dimensions={[
              ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
              ApiDimension.DIMENSION_LOCATION,
              ApiDimension.DIMENSION_LANDING_PAGE_PATH,
              ApiDimension.DIMENSION_PATH,
            ]}
            renderBodyWithDimension={(dimension) => (
              <InsightsBlock dimension={dimension} />
            )}
          />
          <AnalyticsCardGroup
            localStorageKey="metric-explorer-technology-insights"
            title="Technology Insights"
            dimensions={[
              ApiDimension.DIMENSION_DEVICE,
              ApiDimension.DIMENSION_BROWSER,
              ApiDimension.DIMENSION_BROWSER_WITH_VERSION,
              ApiDimension.DIMENSION_OS,
            ]}
            renderBodyWithDimension={(dimension) => (
              <InsightsBlock dimension={dimension} />
            )}
          />
          <AnalyticsCardGroup
            localStorageKey="metric-explorer-geographic-insights"
            title="Geographic Insights"
            dimensions={[
              ApiDimension.DIMENSION_CITY,
              ApiDimension.DIMENSION_REGION,
              ApiDimension.DIMENSION_COUNTRY,
            ]}
            renderBodyWithDimension={(dimension) => (
              <InsightsBlock dimension={dimension} />
            )}
          />
        </Stack>
      ) : (
        <Loading />
      )}
      <MetricExplorerDrawer
        open={open}
        onClose={onClose}
        metric={selectedMetric}
        setMetric={setSelectedMetric}
        attributes={selectedAttributes}
        setAttributes={setSelectedAttributes}
      />
    </>
  );
}

export default MetricExplorer;
